import { parseFolderName } from './parse-folder-name'

/** 作品的頁面來自資料夾還是壓縮檔。加入時決定並保存，不由副檔名推斷。 */
export type ComicKind = 'folder' | 'archive'

const archiveExtensions = ['.zip', '.cbz'] as const

/** 副檔名是否標示為支援的壓縮檔。 */
export function isArchiveName(name: string): boolean {
  const lower = name.toLowerCase()
  return archiveExtensions.some(extension => lower.endsWith(extension))
}

export function stripArchiveExtension(name: string): string {
  const lower = name.toLowerCase()
  const extension = archiveExtensions.find(value => lower.endsWith(value))
  return extension ? name.slice(0, -extension.length) : name
}

/**
 * 最末段的開頭是否已經是作品名。
 *
 * 只比對開頭，不比對包含關係：「獵人外傳 第1卷」這種同前綴但不同作品的情形
 * 應該保留最末段原樣（結果是「獵人外傳 第1卷」，正確），而如果比對整段包含
 * 關係，像「第1卷 獵人特典」這種作品名在中間的情形會被錯誤地判為重複並丟掉
 * 前綴帶來的區分。
 *
 * 忽略大小寫是為了拉丁文字的資料夾：Hunter 與 hunter vol1 應視為同一個作品名，
 * 且此時該採用最末段的大小寫，因為最末段是更完整的那一份。
 */
function startsWithTitle(last: string, title: string): boolean {
  if (!title) return false
  return last.slice(0, title.length).toLocaleLowerCase() === title.toLocaleLowerCase()
}

/**
 * 由相對於來源的路徑推導預設書名與作者。
 *
 * 作者只看第一段——作者標記通常寫在作品那一層，不會寫在卷上。
 * 標題以第一段的推導標題為基礎：最末段的開頭若已經是那個作品名，就直接取
 * 最末段（「獵人/獵人 第1卷」成為「獵人 第1卷」而不是「獵人 獵人 第1卷」）；
 * 否則接上最末段，讓「獵人/第1卷」成為「獵人 第1卷」而不是一堆分不出來的
 * 「第1卷」。中間層（例如「單行本」）刻意丟棄，它通常是分類用的容器而非作品資訊。
 *
 * 不一律只取最末段：那會讓「獵人/第1卷」這種最末段沒有作品名的結構退化成
 * 一堆分不出來的「第1卷」，而那正是接上作品名這條規則存在的理由。
 */
export function deriveComicTitle(relPath: string, kind: ComicKind): { title: string; author: string } {
  const segments = relPath.split('/').filter(Boolean)
  if (!segments.length) return { title: relPath, author: '' }
  const [first] = segments
  const lastRaw = segments[segments.length - 1]
  if (segments.length === 1) {
    return parseFolderName(kind === 'archive' ? stripArchiveExtension(first) : first)
  }
  const base = parseFolderName(first)
  const baseTitle = base.title.trim()
  const last = (kind === 'archive' ? stripArchiveExtension(lastRaw) : lastRaw).trim()
  return {
    title: startsWithTitle(last, baseTitle) ? last : `${baseTitle} ${last}`,
    author: base.author,
  }
}
